/**
 * thumbnailCompression.js
 *
 * Canvas-based image compression utilities for the photo-thumbnail pipeline.
 *
 * Workflow:
 *   1. compressToThumbnail  – resize & convert a File to a 280×280 WebP Blob
 *   2. uploadThumbnail      – upload the Blob to Supabase private storage
 *   3. getSignedThumbnailUrl – generate a short-lived signed URL for display
 *   4. deleteSessionThumbnails – clean up all thumbnails for a given session
 *
 * Constants:
 *   THUMBNAIL_SIZE  – output pixel dimensions (square)
 *   WEBP_QUALITY    – WebP compression quality (0–1)
 */

/** Output size in pixels for both width and height */
const THUMBNAIL_SIZE = 280;

/** WebP quality factor (0 = smallest file, 1 = lossless) */
const WEBP_QUALITY = 0.7;

// ─── Compression ─────────────────────────────────────────────────────────────

/**
 * Compresses a single image File to a square 280×280 WebP thumbnail
 * using the browser Canvas API. The source image is centre-cropped to
 * maintain aspect ratio before scaling.
 *
 * @param {File} file - Original image file (JPEG, PNG, HEIC, …)
 * @returns {Promise<Blob>} Compressed WebP Blob ready for upload
 */
export const compressToThumbnail = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = THUMBNAIL_SIZE;
        canvas.height = THUMBNAIL_SIZE;

        const ctx = canvas.getContext('2d');

        // Centre-crop: take the largest square from the middle of the source image
        const srcSize = Math.min(img.width, img.height);
        const srcX = (img.width - srcSize) / 2;
        const srcY = (img.height - srcSize) / 2;

        ctx.drawImage(
          img,
          srcX, srcY, srcSize, srcSize,   // source rect (cropped square)
          0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE  // destination rect
        );

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Canvas toBlob returned null'));
            }
          },
          'image/webp',
          WEBP_QUALITY
        );
      } catch (err) {
        URL.revokeObjectURL(objectUrl);
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to load image: ${file?.name}`));
    };

    img.src = objectUrl;
  });
};

// ─── Storage ──────────────────────────────────────────────────────────────────

/**
 * Uploads a compressed thumbnail Blob to the `photo-thumbnails` Supabase
 * private storage bucket.
 *
 * File path pattern: `{userId}/{sessionId}/rank-{rank}-{timestamp}.webp`
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {Blob}   thumbnailBlob - Compressed WebP Blob from compressToThumbnail
 * @param {string} userId        - Authenticated user's UUID
 * @param {string} sessionId     - Analysis session UUID (used for folder grouping)
 * @param {number} rank          - Photo rank (1, 2, 3, …)
 * @returns {Promise<string>} Storage path of the uploaded file
 */
export const uploadThumbnail = async (supabase, thumbnailBlob, userId, sessionId, rank) => {
  const fileName = `${userId}/${sessionId}/rank-${rank}-${Date.now()}.webp`;

  const { data, error } = await supabase?.storage
    ?.from('photo-thumbnails')
    ?.upload(fileName, thumbnailBlob, {
      contentType: 'image/webp',
      cacheControl: '3600',
      upsert: false,
    });

  if (error) {
    throw new Error(`Thumbnail upload failed: ${error?.message}`);
  }

  return data?.path;
};

/**
 * Generates a short-lived signed URL for a private thumbnail (expires in 1 hour).
 * Use this URL directly in <img> src attributes — it will expire after 3600 seconds.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} filePath - Storage path returned by uploadThumbnail
 * @returns {Promise<string>} Signed URL valid for 3600 seconds
 */
export const getSignedThumbnailUrl = async (supabase, filePath) => {
  const { data, error } = await supabase?.storage
    ?.from('photo-thumbnails')
    ?.createSignedUrl(filePath, 3600);

  if (error || !data?.signedUrl) {
    throw new Error(`Failed to generate signed URL: ${error?.message}`);
  }

  return data?.signedUrl;
};

/**
 * Deletes all thumbnail files for a given session folder.
 * Called after an analysis session is removed to free storage space.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} userId    - Authenticated user's UUID
 * @param {string} sessionId - Analysis session UUID
 */
export const deleteSessionThumbnails = async (supabase, userId, sessionId) => {
  const folderPath = `${userId}/${sessionId}`;

  // List all files in the session folder before deleting
  const { data: files } = await supabase?.storage
    ?.from('photo-thumbnails')
    ?.list(folderPath);

  if (files?.length > 0) {
    const paths = files?.map((f) => `${folderPath}/${f?.name}`);
    await supabase?.storage?.from('photo-thumbnails')?.remove(paths);
  }
};
